/**
 * store/ — IndexedDB persistence for "the save IS the world".
 *
 * Public surface:
 *  - newSave()                  build a structurally valid initial Save
 *  - saveGame / writeSave       persist the live world (single slot)
 *  - loadGame / loadSave        read + migrate the save (null if absent/corrupt)
 *  - exportSave / importSave     JSON round-trip to/from a backup file
 *  - MIGRATIONS                  the version-keyed migration registry
 *
 * The load path is defensive: a missing or corrupt save resolves to `null` rather
 * than throwing, so a tired player never hits a crash on open. Import (a user-picked
 * file) is the one place a bad value surfaces a friendly error.
 */
import type { Save, SaveMigration } from '@/shared';
import { SCHEMA_VERSION } from '@/shared';

import {
  loadSaveFromDb,
  writeSaveToDb,
  resetDb,
  writeRaw,
  listSavesInDb,
  loadSlotFromDb,
  saveSlotToDb,
  deleteSlotFromDb,
} from './db.js';
import {
  MIGRATIONS,
  migrate,
  fillNaturalMoveset,
  setMigrationDataLookup,
  _registerMigration,
  _resetMigrations,
} from './migrations.js';

export { SCHEMA_VERSION, MIGRATIONS, fillNaturalMoveset, setMigrationDataLookup };
export type { MigrationDataLookup } from './migrations.js';
export { newSave } from './newSave.js';
export type { NewSaveOptions } from './newSave.js';
export { DEFAULT_SETTINGS, DEFAULT_AVATAR } from './newSave.js';
export { exportSave, importSave, saveToBlob, importFromBlob } from './exportImport.js';
export {
  getServerSave,
  putServerSave,
  listServerSaves,
  deleteServerSave,
  debouncePut,
  DEFAULT_SLOT,
  type ServerSaveDeps,
  type DebouncedPut,
} from './serverSave.js';

// ── proxy password gate (the missing client half of POST /api/auth) ─────────────
export {
  authenticate,
  probeAuthPosture,
  readProxyToken,
  storeProxyToken,
  clearProxyToken,
  hasProxyToken,
  PROXY_TOKEN_KEY,
  type AuthDeps,
  type AuthPosture,
} from './auth.js';

// ── multi-slot IndexedDB surface (D16) ─────────────────────────────────────────
export { listSavesInDb, loadSlotFromDb, saveSlotToDb, deleteSlotFromDb };

/**
 * Persist the save to its own IndexedDB slot (keyed by `save.profile.slotId`).
 * Back-compat alias retained for callers that don't yet route by slot.
 */
export async function saveGame(save: Save): Promise<void> {
  await writeSaveToDb(save);
}

/**
 * Load the most-recent save from IndexedDB (back-compat single-slot read), migrating
 * up to SCHEMA_VERSION; null if absent/corrupt. Multi-slot callers use {@link
 * listSavesInDb} / {@link loadSlotFromDb}.
 */
export async function loadGame(): Promise<Save | null> {
  return loadSaveFromDb();
}

/** Alias retained for the frozen store stub surface. */
export const writeSave = saveGame;
/** Alias retained for the frozen store stub surface. */
export const loadSave = loadGame;

/** Internal hooks for the test suite only — not part of the runtime contract. */
export const __testing = {
  resetDbForTests: resetDb,
  writeRawForTests: writeRaw,
  registerMigrationForTests: (from: number, fn: SaveMigration): void =>
    _registerMigration(from, fn),
  migrateForTests: (raw: unknown, fromVersion?: number): Save => migrate(raw, fromVersion),
  resetMigrationsForTests: _resetMigrations,
};
